import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Search, Filter, UserPlus, Mail, Phone, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';

const Personnel: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: 'Pilot',
    rank: 'First Officer',
    base: 'BOS',
    status: 'active',
    license_number: '',
    medical_class: 'Class 1',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { data, loading, error, refetch } = useApi(
    () => apiService.getCrewMembers({
      status: statusFilter === 'all' ? undefined : statusFilter,
      page: currentPage,
      per_page: 20
    }),
    [statusFilter, currentPage]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.createCrewMember(formData);
      setShowAddModal(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        position: 'Pilot',
        rank: 'First Officer',
        base: 'BOS',
        status: 'active',
        license_number: '',
        medical_class: 'Class 1',
        emergency_contact_name: '',
        emergency_contact_phone: '',
      });
      setFormErrors({});
      refetch();
    } catch (err: any) {
      console.error('Failed to create crew member:', err);
      if (err.errors) {
        setFormErrors(err.errors);
      } else {
        alert(err.message || 'Failed to add personnel. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'badge-active';
      case 'inactive':
        return 'badge-inactive';
      case 'on-leave':
        return 'badge-pending';
      default:
        return 'badge-inactive';
    }
  };

  const filteredCrew = data?.data.filter((member) =>
    `${member.first_name} ${member.last_name} ${member.employee_id}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Personnel</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage crew members and their information
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <UserPlus className="h-5 w-5" />
          Add Personnel
        </button>
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or employee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field w-40"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="on-leave">On Leave</option>
            </select>

            <button
              onClick={() => alert('Advanced filters coming soon')}
              className="btn-secondary flex items-center gap-2"
            >
              <Filter className="h-5 w-5" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Personnel Table */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="glass-card p-6 text-center text-red-400">{error.message}</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Position</th>
                <th>Rank</th>
                <th>Base</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCrew?.map((member) => (
                <tr key={member.id}>
                  <td className="font-mono text-sky-400">{member.employee_id}</td>
                  <td>
                    <Link
                      to={`/personnel/${member.id}`}
                      className="font-medium text-white hover:text-sky-400"
                    >
                      {member.first_name} {member.last_name}
                    </Link>
                  </td>
                  <td>{member.position}</td>
                  <td>
                    <span className="badge-sky">{member.rank}</span>
                  </td>
                  <td>{member.base}</td>
                  <td>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Mail className="h-3 w-3" />
                        {member.email}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Phone className="h-3 w-3" />
                        {member.phone}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={getStatusBadge(member.status)}>{member.status}</span>
                  </td>
                  <td>
                    <Link
                      to={`/personnel/${member.id}`}
                      className="text-sm text-sky-400 hover:text-sky-300"
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between glass-card p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Page {data.page} of {data.total_pages} ({data.total} total results)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-secondary px-3 py-1 text-sm flex items-center gap-1 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(data.total_pages, currentPage + 1))}
              disabled={currentPage >= data.total_pages}
              className="btn-secondary px-3 py-1 text-sm flex items-center gap-1 disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add Personnel Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New Personnel</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className={`input-field ${formErrors.first_name ? 'border-red-500' : ''}`}
                      placeholder="John"
                    />
                    {formErrors.first_name && <p className="text-xs text-red-400 mt-1">{formErrors.first_name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className={`input-field ${formErrors.last_name ? 'border-red-500' : ''}`}
                      placeholder="Smith"
                    />
                    {formErrors.last_name && <p className="text-xs text-red-400 mt-1">{formErrors.last_name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`input-field ${formErrors.email ? 'border-red-500' : ''}`}
                      placeholder="john.smith@bermudair.com"
                    />
                    {formErrors.email && <p className="text-xs text-red-400 mt-1">{formErrors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className={`input-field ${formErrors.phone ? 'border-red-500' : ''}`}
                      placeholder="+1-441-555-0100"
                    />
                    {formErrors.phone && <p className="text-xs text-red-400 mt-1">{formErrors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Role Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Role Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Position *</label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      <option value="Pilot">Pilot</option>
                      <option value="Cabin Crew">Cabin Crew</option>
                      <option value="Ground Staff">Ground Staff</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Rank *</label>
                    <select
                      name="rank"
                      value={formData.rank}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      <option value="Captain">Captain</option>
                      <option value="First Officer">First Officer</option>
                      <option value="Training Captain">Training Captain</option>
                      <option value="Senior Cabin Crew">Senior Cabin Crew</option>
                      <option value="Purser">Purser</option>
                      <option value="Cabin Crew">Cabin Crew</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Base *</label>
                    <select
                      name="base"
                      value={formData.base}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      <option value="BOS">BOS - Bermuda</option>
                      <option value="TBPB">TBPB - Barbados</option>
                      <option value="TNCM">TNCM - St Maarten</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* License & Medical */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">License & Medical</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">License Number</label>
                    <input
                      type="text"
                      name="license_number"
                      value={formData.license_number}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="ATP-12345"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Medical Class</label>
                    <select
                      name="medical_class"
                      value={formData.medical_class}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      <option value="Class 1">Class 1</option>
                      <option value="Class 2">Class 2</option>
                      <option value="Class 3">Class 3</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Contact Name</label>
                    <input
                      type="text"
                      name="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Contact Phone</label>
                    <input
                      type="tel"
                      name="emergency_contact_phone"
                      value={formData.emergency_contact_phone}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="+1-441-555-0101"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Personnel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Personnel;
